
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { whatsappService } from "@/services/whatsappService";
import { useServerStatus } from "@/hooks/useServerStatus";
import { QRCodeDisplay } from "./whatsapp/QRCodeDisplay";
import { ConnectionStatus } from "./whatsapp/ConnectionStatus";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

export const WhatsAppConnection = () => {
  const { toast } = useToast();
  const { whatsappConnected, refetch } = useServerStatus();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [qrCodeData, setQrCodeData] = useState("");
  const [connectionInitiated, setConnectionInitiated] = useState(false);
  const [connectedNumber, setConnectedNumber] = useState<string>("");
  const { currentUser } = useAuth();
  const [companyId, setCompanyId] = useState<string | undefined>(undefined);
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);

  // Ensure we have the company ID even if auth state is temporarily lost
  useEffect(() => {
    const getCompanyId = async () => {
      setIsLoadingCompany(true);
      
      try {
        // First try to get current user session to get user-specific company ID
        const { data: { session } } = await supabase.auth.getSession();
        const userSpecificKey = session?.user?.id ? `companyId_${session.user.id}` : null;
        
        console.log('WhatsAppConnection: Current user session:', session?.user?.id);
        
        // First priority: Use the current user's company ID
        if (currentUser?.companyId) {
          console.log('WhatsAppConnection: Setting company ID from currentUser:', currentUser.companyId);
          setCompanyId(currentUser.companyId);
          setIsLoadingCompany(false);
          return;
        }
        
        // Second priority: Use the user-specific company ID from localStorage
        if (userSpecificKey && localStorage.getItem(userSpecificKey)) {
          const userCompanyId = localStorage.getItem(userSpecificKey);
          console.log('WhatsAppConnection: Setting company ID from user-specific key:', userCompanyId);
          setCompanyId(userCompanyId || undefined);
          setIsLoadingCompany(false);
          return;
        }
        
        // Last resort: Fallback to general localStorage if needed
        const savedCompanyId = localStorage.getItem('currentCompanyId');
        if (savedCompanyId) {
          console.log('WhatsAppConnection: Setting company ID from localStorage:', savedCompanyId);
          setCompanyId(savedCompanyId);
          setIsLoadingCompany(false);
        } else {
          console.error('WhatsAppConnection: No company ID available from any source');
          setCompanyId(undefined);
          setIsLoadingCompany(false);
        }
      } catch (error) {
        console.error('WhatsAppConnection: Error getting company ID:', error);
        setCompanyId(undefined);
        setIsLoadingCompany(false);
      }
    };
    
    getCompanyId();
  }, [currentUser?.companyId]);

  // Verify the company ID is available and check the connection status
  useEffect(() => {
    if (!companyId) {
      console.error('WhatsAppConnection: Company ID not available');
      setIsConnected(false);
      setQrCodeData("");
      setConnectedNumber("");
      setConnectionInitiated(false);
      return;
    }
    
    // Check initial connection status for this specific company
    const checkInitialStatus = async () => {
      try {
        console.log('WhatsAppConnection: Checking WhatsApp status for company:', companyId);
        const status = await whatsappService.checkStatus(companyId);
        
        console.log('WhatsAppConnection: Initial status for company', companyId, ':', status);
        setIsConnected(status.connected);
        
        if (status.qrCode) {
          setQrCodeData(status.qrCode);
          setConnectionInitiated(true);
        }
        
        if (status.phoneNumber) {
          console.log('WhatsAppConnection: Connected number found:', status.phoneNumber);
          setConnectedNumber(status.phoneNumber);
        }
      } catch (error) {
        console.error('WhatsAppConnection: Error checking initial status for company', companyId, ':', error);
      }
    };
    
    checkInitialStatus();
  }, [companyId]);

  // Update connection status when server status changes
  useEffect(() => {
    if (companyId) {
      setIsConnected(whatsappConnected);
    }
  }, [whatsappConnected, companyId]);

  const handleConnect = async () => {
    if (isLoading || !companyId) {
      console.error('WhatsAppConnection: Cannot connect: isLoading=', isLoading, 'companyId=', companyId);
      return;
    }
    
    try {
      setIsLoading(true);
      setConnectionInitiated(true);
      setQrCodeData("");
      setConnectedNumber("");
      
      // Ensure any previous connection for this company is properly closed
      await whatsappService.disconnect(companyId);
      
      console.log('WhatsAppConnection: Starting WhatsApp connection for company:', companyId);
      await whatsappService.initialize(
        companyId,
        (qr) => {
          console.log('WhatsAppConnection: QR Code received for company', companyId, ':', qr ? 'yes' : 'no');
          if (qr) {
            setQrCodeData(qr);
            setIsConnected(false);
          }
        },
        () => {
          console.log('WhatsAppConnection: WhatsApp connected for company', companyId);
          setIsConnected(true);
          setQrCodeData("");
          refetch();
          toast({
            title: "WhatsApp conectado",
            description: "O WhatsApp da sua empresa foi conectado com sucesso!",
          });
        },
        (message) => {
          console.log('WhatsAppConnection: New message received for company', companyId, ':', message);
        },
        (phone) => {
          console.log('WhatsAppConnection: Connected phone for company', companyId, ':', phone);
          if (phone) {
            setConnectedNumber(phone);
          }
        }
      );
    } catch (error) {
      console.error('WhatsAppConnection: Connection error for company', companyId, ':', error);
      toast({
        title: "Erro na conexão",
        description: error instanceof Error ? error.message : "Não foi possível conectar ao WhatsApp",
        variant: "destructive",
      });
      setIsConnected(false);
      setConnectionInitiated(false);
      setQrCodeData("");
      setConnectedNumber("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!companyId) {
      console.error('WhatsAppConnection: Cannot disconnect: No company ID available');
      return;
    }
    
    try {
      console.log('WhatsAppConnection: Disconnecting WhatsApp for company:', companyId);
      await whatsappService.disconnect(companyId);
      setIsConnected(false);
      setQrCodeData("");
      setConnectionInitiated(false);
      setConnectedNumber("");
      refetch();
      toast({
        title: "WhatsApp desconectado",
        description: "O WhatsApp da sua empresa foi desconectado com sucesso.",
      });
    } catch (error) {
      console.error('WhatsAppConnection: Disconnect error for company', companyId, ':', error);
      toast({
        title: "Erro ao desconectar",
        description: "Ocorreu um erro ao desconectar o WhatsApp da sua empresa.",
        variant: "destructive",
      });
    }
  };

  if (isLoadingCompany) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conexão com WhatsApp</CardTitle>
          <CardDescription>
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary mr-2"></div>
              Carregando dados da empresa...
            </div>
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!companyId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Conexão com WhatsApp</CardTitle>
          <CardDescription className="text-amber-600">
            Empresa não identificada. Contate o administrador.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conexão com WhatsApp</CardTitle>
        {connectedNumber ? (
          <CardDescription className="text-green-600 font-medium">
            Conectado ao número: {connectedNumber} 
            <span className="text-muted-foreground ml-2 text-xs">
              (Empresa: {companyId.slice(0, 8)}...)
            </span>
          </CardDescription>
        ) : (
          <CardDescription className="text-muted-foreground">
            Empresa: {companyId.slice(0, 8)}...
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col items-center space-y-4">
          {(!isConnected && qrCodeData && connectionInitiated) && (
            <QRCodeDisplay qrCodeData={qrCodeData} />
          )}
          <ConnectionStatus
            isConnected={isConnected}
            isLoading={isLoading}
            onConnect={handleConnect}
            onDisconnect={handleDisconnect}
            showQRCode={!!qrCodeData}
          />
        </div>
      </CardContent>
    </Card>
  );
};
